import React, { useMemo } from "react";
import PropTypes from "prop-types";
import "./MonthFilter.css";

const normalizeOption = (option, labelKey, valueKey) => {
  if (typeof option === "string") {
    return { label: option, value: option, disabled: false };
  }

  return {
    label: option?.[labelKey] ?? option?.label ?? option?.value ?? "",
    value: option?.[valueKey] ?? option?.value ?? option?.label ?? "",
    disabled: Boolean(option?.disabled),
  };
};

function MonthFilter(props) {
  const {
    value,
    onChange,
    options = [],
    labelKey = "label",
    valueKey = "value",
    className = "",
    disabled = false,
    ariaLabel = "Select date filter",
  } = props;

  const normalizedOptions = useMemo(
    () =>
      options
        .map((option) => normalizeOption(option, labelKey, valueKey))
        .filter((option) => option.label && option.value),
    [labelKey, options, valueKey],
  );

  const handleSelect = (optionValue, option) => {
    if (disabled || option.disabled) return;
    if (optionValue === value && optionValue !== "custom") return;
    onChange(optionValue, option);
  };

  return (
    <div
      className={`month-filter ${className}`.trim()}
      role="radiogroup"
      aria-label={ariaLabel}
    >
      {normalizedOptions.map((option) => {
        const isSelected = value === option.value || value === option.label;

        return (
          <button
            key={option.value}
            type="button"
            className={`month-filter__option ${
              isSelected ? "month-filter__option--active" : ""
            }`}
            onClick={() => handleSelect(option.value, option)}
            disabled={disabled || option.disabled}
            role="radio"
            aria-checked={isSelected}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

MonthFilter.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.string,
        disabled: PropTypes.bool,
      }),
    ]),
  ),
  labelKey: PropTypes.string,
  valueKey: PropTypes.string,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  ariaLabel: PropTypes.string,
};

export default MonthFilter;
